import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import PageHeader, { EmptyState } from "@/components/PageHeader";
import StatusChip from "@/components/StatusChip";
import SigningPanel from "@/components/legal/SigningPanel";
import LegalEditForm from "@/components/legal/LegalEditForm";
import RecordActions from "@/components/RecordActions";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Plus, Sparkle, Scales, PencilSimple } from "@phosphor-icons/react";

export default function Legal() {
  const [params, setParams] = useSearchParams();
  const [editing, setEditing] = useState(false);
  const [docs, setDocs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [tab, setTab] = useState("nda");
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ doc_type: "nda", nda_type: "mutual", purpose: "", jurisdiction: "Mumbai, India", validity_years: 3, confidentiality_period_years: 3, effective_date: new Date().toISOString().slice(0,10), disclosing_party: { contact_id: "" }, receiving_party: { contact_id: "" } });

  const load = async () => {
    try {
      const [{ data }, { data: cs }] = await Promise.all([
        api.get(`/legal?doc_type=${tab}`),
        api.get("/contacts"),
      ]);
      setDocs(data); setContacts(cs);
    } catch (e) {
      if (e?.response?.status === 403) toast.error("Legal access requires legal_manager role");
      else toast.error("Load failed");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  useEffect(() => {
    const id = params.get("doc");
    if (params.get("esign") === "completed" && id) {
      toast.success("Aadhaar eSign completed — signature embedded", { id: "esign-done" });
      api.get(`/legal/${id}`).then(({ data }) => setSelected(data)).catch(() => {});
      setParams({}, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  const reloadSelected = async (id) => {
    const { data } = await api.get(`/legal/${id}`); setSelected(data); load();
  };

  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/legal", form); toast.success("Legal document created"); setShowNew(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const aiClauses = async (id) => {
    try { await api.post(`/legal/${id}/ai-clauses`); toast.success("AI clauses generated"); if (selected?._id === id) reloadSelected(id); else load(); }
    catch { toast.error("AI failed"); }
  };

  return (
    <div data-testid="legal-page" className="page-fade">
      <PageHeader eyebrow="Module" title="Legal department" description="NDAs & MOUs with PAdES digital signing — DSC (USB token) and Aadhaar eSign — plus embedded-signature verification." tint="plum" icon={Scales} actions={
        <button className="btn-primary" onClick={()=>setShowNew(true)} data-testid="new-legal-btn"><Plus size={14}/> New {tab.toUpperCase()}</button>
      }/>
      <div className="tab-list mb-6">
        {[["nda","NDA"],["mou","MOU"]].map(([v,l])=>(
          <button key={v} onClick={()=>setTab(v)} data-testid={`legal-tab-${v}`} className={tab===v?"is-active":""}>{l}</button>
        ))}
      </div>

      <div className="card-soft p-0 overflow-x-auto">
        <table className="table-modern">
          <thead><tr><th>Number</th><th>Type</th><th>Parties</th><th>Effective</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {docs.map((d) => (
              <tr key={d._id} data-testid={`legal-row-${d.doc_number}`}>
                <td className="font-mono text-xs">{d.doc_number}</td>
                <td className="text-sm">{d.doc_type.toUpperCase()} {d.doc_type==="nda"&&`(${d.nda_type})`}</td>
                <td className="text-sm">{d.disclosing_party?.company || d.disclosing_party?.name || (d.parties||[]).map(p=>p.name).join(" ↔ ") || "—"}</td>
                <td className="text-xs text-muted-foreground">{d.effective_date || "—"}</td>
                <td><StatusChip value={d.status}/></td>
                <td className="text-right whitespace-nowrap"><button onClick={()=>setSelected(d)} className="btn-ghost btn-sm" data-testid={`legal-open-${d.doc_number}`}>Open</button></td>
              </tr>
            ))}
            {!docs.length && <tr><td colSpan={6}><EmptyState icon={Scales} title={`No ${tab.toUpperCase()}s yet`} description="Create your first document to begin the signing workflow." /></td></tr>}
          </tbody>
        </table>
      </div>

      {selected && createPortal(
        <div className="fixed inset-0 z-40 modal-backdrop flex fade-in" onClick={()=>setSelected(null)}>
          <div className="ml-auto w-full max-w-3xl h-full bg-card border-l border-border overflow-y-auto drawer-slide" onClick={(e)=>e.stopPropagation()} data-testid="legal-drawer">
            <div className="p-7 border-b border-border sticky top-0 bg-card z-10">
              <div className="label-eyebrow">{selected.doc_type.toUpperCase()}</div>
              <div className="display-2 mt-2">{selected.doc_number}</div>
              <div className="flex gap-2 mt-4"><StatusChip value={selected.status}/></div>
              <div className="flex gap-2 mt-5 flex-wrap">
                {!["fully_signed","sent_for_signature","partially_signed"].includes(selected.status) && <button onClick={()=>setEditing(true)} className="btn-ghost btn-sm" data-testid="legal-edit-btn"><PencilSimple size={12}/> Edit details</button>}
                {selected.doc_type === "nda" && !selected.current_pdf_id && <button onClick={()=>aiClauses(selected._id)} className="btn-ghost btn-sm" data-testid="legal-ai-clauses"><Sparkle size={12}/> AI clauses</button>}
                <RecordActions entity="legal_documents" record={selected} canEdit={false} compact={false} onChanged={()=>{ setSelected(null); load(); }} />
              </div>
              {selected.signing_mocked && selected.signatories?.length > 0 && <div className="mt-4 text-xs text-[color:hsl(var(--warning))]" data-testid="sandbox-warning">Signed with a SANDBOX test certificate / simulated ESP — not legally valid. Switch LEGAL_SIGNING_MODE / ESIGN_MODE for production.</div>}
            </div>
            <div className="p-7">
              <pre className="whitespace-pre-wrap text-[13.5px] font-mono leading-relaxed bg-muted/40 rounded-lg p-5 border border-border/60">{selected.body_html}</pre>
              <SigningPanel doc={selected} onChanged={()=>reloadSelected(selected._id)} />
              {selected.signatories?.length > 0 && (
                <div className="mt-8">
                  <div className="label-eyebrow mb-3">Signatures</div>
                  {selected.signatories.map((s, i) => (
                    <div key={i} className="text-sm border-b border-border/60 py-2.5" data-testid={`signatory-${i}`}>
                      <span className="font-mono">{s.name}</span> · {s.designation || "—"} · <span className="text-muted-foreground">{s.signature_type}{s.mocked ? " (sandbox)" : ""} · {fmtDate(s.signed_at)}</span>
                      {s.certificate && <div className="text-xs text-muted-foreground mt-1">{s.certificate.subject}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>, document.body)}
      {editing && selected && <LegalEditForm doc={selected} onClose={()=>setEditing(false)} onSaved={(d)=>{ setSelected(d); load(); }} />}

      {showNew && (
        <div className="fixed inset-0 z-50 modal-backdrop flex items-center justify-center p-4 fade-in" onClick={()=>setShowNew(false)}>
          <form onSubmit={submit} onClick={(e)=>e.stopPropagation()} className="modal-surface modal-scale p-7 w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-legal-form">
            <div className="label-eyebrow">New {tab.toUpperCase()}</div>
            <div className="display-3 mt-1 mb-6">Create</div>
            <div className="grid grid-cols-2 gap-5">
              <label className="block"><span className="label-eyebrow">Type</span>
                <select value={form.doc_type} onChange={(e)=>setForm({...form,doc_type:e.target.value})}
                        className="input-soft mt-2" data-testid="legal-input-type">
                  <option value="nda">NDA</option><option value="mou">MOU</option>
                </select></label>
              {form.doc_type === "nda" && (
                <label className="block"><span className="label-eyebrow">NDA type</span>
                  <select value={form.nda_type} onChange={(e)=>setForm({...form,nda_type:e.target.value})}
                          className="input-soft mt-2" data-testid="legal-input-ndatype">
                    <option value="mutual">Mutual</option><option value="one_way">One-way</option>
                  </select></label>
              )}
              <label className="block"><span className="label-eyebrow">Effective date</span>
                <input type="date" value={form.effective_date} onChange={(e)=>setForm({...form,effective_date:e.target.value})}
                       className="input-underline" data-testid="legal-input-effective"/></label>
              <label className="block"><span className="label-eyebrow">Confidentiality (years)</span>
                <input type="number" value={form.confidentiality_period_years} onChange={(e)=>setForm({...form,confidentiality_period_years:Number(e.target.value)})}
                       className="input-underline"/></label>
            </div>
            <label className="block mt-5"><span className="label-eyebrow">Purpose</span>
              <input value={form.purpose} onChange={(e)=>setForm({...form,purpose:e.target.value})}
                     className="input-underline" data-testid="legal-input-purpose"/></label>
            {form.doc_type === "nda" && (
              <>
                <label className="block mt-5"><span className="label-eyebrow">Disclosing party (from contacts)</span>
                  <select value={form.disclosing_party.contact_id} onChange={(e)=>setForm({...form, disclosing_party:{contact_id:e.target.value}})}
                          className="input-soft mt-2" data-testid="legal-input-disclosing">
                    <option value="">— Select —</option>
                    {contacts.map(c=><option key={c._id} value={c._id}>{c.name} · {c.company} · {c.contact_type}</option>)}
                  </select></label>
                <label className="block mt-5"><span className="label-eyebrow">Receiving party</span>
                  <select value={form.receiving_party.contact_id} onChange={(e)=>setForm({...form, receiving_party:{contact_id:e.target.value}})}
                          className="input-soft mt-2" data-testid="legal-input-receiving">
                    <option value="">— Select —</option>
                    {contacts.map(c=><option key={c._id} value={c._id}>{c.name} · {c.company} · {c.contact_type}</option>)}
                  </select></label>
              </>
            )}
            <div className="flex gap-2 mt-8">
              <button type="button" onClick={()=>setShowNew(false)} className="btn-ghost flex-1">Cancel</button>
              <button type="submit" className="btn-primary flex-1" data-testid="legal-submit">Create document</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
